import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { getClientMonitoredCriticalElements } from 'api/generated/critical-elements-monitoring/critical-elements-monitoring';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { toCriticalElementSummary } from 'containers/user/critical-elements-monitoring/helpers/monitoring-dto-mappers';
import type { CriticalElementSummary } from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export const monitoringCriticalElementsKeys = {
  all: ['monitoringCriticalElements'] as const,
  list: (clientId: string, enteringClass: number | null) =>
    [...monitoringCriticalElementsKeys.all, clientId, enteringClass] as const,
};

export const useMonitoringCriticalElements = (
  clientId: string | undefined,
  enteringClass: number | null
): UseQueryResult<CriticalElementSummary[], Error> => {
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId;

  return useQuery({
    queryKey: monitoringCriticalElementsKeys.list(
      clientId ?? '',
      enteringClass
    ),
    queryFn: async () => {
      if (enteringClass === null) {
        throw new Error(
          'useMonitoringCriticalElements: enteringClass is required'
        );
      }

      const dtos = await getClientMonitoredCriticalElements(
        userId as string,
        Number(clientId),
        { enteringClass }
      );

      return dtos.map(toCriticalElementSummary);
    },
    enabled: !!clientId && !!userId && enteringClass !== null,
  });
};

export default useMonitoringCriticalElements;
